import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useFeatures } from '../hooks/useFeatures';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { PushNotificationSettings } from '../components/PushNotificationSettings';
import { TierBadge } from '../components/UpgradePrompt';
import { 
  Sun, 
  Moon,
  Sparkle,
  Crown,
  User,
  Gear,
  GearSix,
  Trash,
  Warning,
  SpinnerGap,
  Envelope,
  CalendarBlank,
  CheckCircle,
} from '@phosphor-icons/react';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SettingsPage = () => {
  const { user, updateUser, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { tier } = useFeatures();
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [emailReminder, setEmailReminder] = useState(true);
  const [weeklyDigest, setWeeklyDigest] = useState(true);
  const [testDate, setTestDate] = useState('');
  const [testDateSaving, setTestDateSaving] = useState(false);
  const [testDateSaved, setTestDateSaved] = useState(false);

  useEffect(() => {
    if (user) {
      const prefs = user.notification_preferences || {};
      setEmailReminder(prefs.email_reminder !== false);
      setWeeklyDigest(prefs.weekly_digest !== false);
      setTestDate(user.test_date || '');
    }
  }, [user]);

  const handleAIToggle = async (enabled) => {
    if (tier !== 'premium' && enabled) return;
    setLoading(true);
    try {
      await axios.put(`${API}/settings`, { ai_enabled: enabled }, { withCredentials: true });
      updateUser({ ai_enabled: enabled });
    } catch (error) {
      console.error('Failed to update settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTestDateSave = async (dateVal) => {
    setTestDateSaving(true);
    setTestDateSaved(false);
    try {
      await axios.put(`${API}/settings`, { test_date: dateVal }, { withCredentials: true });
      updateUser({ test_date: dateVal || null });
      setTestDateSaved(true);
      setTimeout(() => setTestDateSaved(false), 2000);
    } catch (error) {
      console.error('Failed to update test date:', error);
    } finally {
      setTestDateSaving(false);
    }
  };

  const handleResetStats = async () => {
    setResetting(true);
    try {
      await axios.post(`${API}/progress/reset`, {}, { withCredentials: true });
      await refreshUser();
      setShowResetConfirm(false);
    } catch (error) {
      console.error('Failed to reset stats:', error);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-8" data-testid="settings-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">
            Preferences
          </p>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tighter">
            Settings
          </h1>
        </div>

        {/* Profile Section */}
        <div className="bg-card border border-border rounded-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <User weight="fill" className="w-5 h-5 text-muted-foreground" />
            <h2 className="font-heading font-bold text-lg">Profile</h2>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Name</span>
              <span className="font-medium">{user?.name}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Email</span>
              <span className="font-medium">{user?.email}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Subscription</span>
              <TierBadge tier={tier} />
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-muted-foreground">Member Since</span>
              <span className="font-medium">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Test Date */}
        <div className="bg-card border border-border rounded-sm p-6 mb-6" data-testid="test-date-section">
          <div className="flex items-center gap-3 mb-4">
            <CalendarBlank weight="fill" className="w-5 h-5 text-muted-foreground" />
            <h2 className="font-heading font-bold text-lg">Theory Test Date</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Set your DVSA theory test date to get a personalised countdown and study reminders.
          </p>
          <div className="flex items-center gap-3">
            <input
              type="date"
              value={testDate}
              min={new Date().toISOString().split('T')[0]}
              onChange={(e) => setTestDate(e.target.value)}
              className="flex-1 bg-muted border border-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
              data-testid="test-date-input"
            />
            <Button
              size="sm"
              onClick={() => handleTestDateSave(testDate)}
              disabled={testDateSaving}
              className="rounded-sm"
              data-testid="save-test-date-btn"
            >
              {testDateSaving ? (
                <SpinnerGap className="w-4 h-4 animate-spin" />
              ) : testDateSaved ? (
                <><CheckCircle weight="fill" className="w-4 h-4 mr-1" /> Saved</>
              ) : (
                'Save'
              )}
            </Button>
            {testDate && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setTestDate(''); handleTestDateSave(''); }}
                className="rounded-sm text-muted-foreground"
                data-testid="clear-test-date-btn"
              >
                Clear
              </Button>
            )}
          </div>
          {testDate && (
            <p className="text-xs text-muted-foreground mt-2">
              Your test: {new Date(testDate + 'T00:00:00').toLocaleDateString('en-GB', {
                weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
              })}
            </p>
          )}
        </div>

        {/* Appearance */}
        <div className="bg-card border border-border rounded-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Gear weight="fill" className="w-5 h-5 text-muted-foreground" />
            <h2 className="font-heading font-bold text-lg">Appearance</h2>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {theme === 'dark' ? (
                <Moon weight="fill" className="w-5 h-5 text-accent" />
              ) : (
                <Sun weight="fill" className="w-5 h-5 text-amber-500" />
              )}
              <div>
                <Label className="font-medium">Dark Mode</Label>
                <p className="text-xs text-muted-foreground">Switch between light and dark themes</p>
              </div>
            </div>
            <Switch 
              checked={theme === 'dark'} 
              onCheckedChange={toggleTheme}
              data-testid="theme-toggle"
            />
          </div>
        </div>

        {/* Push Notifications */}
        <div className="mb-6">
          <PushNotificationSettings />
        </div>

        {/* Email Reminders */}
        <div className="bg-card border border-border rounded-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Envelope weight="fill" className="w-5 h-5 text-muted-foreground" />
            <h2 className="font-heading font-bold text-lg">Email Notifications</h2>
          </div>
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">Daily Reminders</Label>
                <p className="text-xs text-muted-foreground">
                  Study reminders via email when push notifications aren't active
                </p>
              </div>
              <Switch
                checked={emailReminder}
                onCheckedChange={async (checked) => {
                  setEmailReminder(checked);
                  try {
                    await axios.put(`${API}/notifications/email-preferences`, {
                      email_reminder: checked,
                    }, { withCredentials: true });
                  } catch (err) {
                    console.error('Failed to update email preferences:', err);
                  }
                }}
                data-testid="email-reminder-toggle"
              />
            </div>
            <div className="border-t border-border pt-5 flex items-center justify-between">
              <div>
                <Label className="font-medium">Weekly Digest</Label>
                <p className="text-xs text-muted-foreground">
                  Receive a weekly summary of your progress, weak areas, and streak milestones every Monday
                </p>
              </div>
              <Switch
                checked={weeklyDigest}
                onCheckedChange={async (checked) => {
                  setWeeklyDigest(checked);
                  try {
                    await axios.put(`${API}/notifications/email-preferences`, {
                      weekly_digest: checked,
                    }, { withCredentials: true });
                  } catch (err) {
                    console.error('Failed to update digest preference:', err);
                  }
                }}
                data-testid="weekly-digest-toggle"
              />
            </div>
          </div>
        </div>

        {/* Subscription Plans */}
        <div className="bg-card border border-border rounded-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Crown weight="fill" className={cn("w-5 h-5", tier === 'premium' ? "text-accent" : tier === 'standard' ? "text-blue-400" : "text-muted-foreground")} />
            <h2 className="font-heading font-bold text-lg">Subscription</h2>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm text-muted-foreground">Current plan:</span>
                <TierBadge tier={tier} />
              </div>
              <p className="text-xs text-muted-foreground">
                {tier === 'premium' ? 'You have full access to all features' : 'Upgrade to unlock more features and categories'}
              </p>
            </div>
            <Link to="/subscription">
              <Button variant={tier === 'premium' ? 'outline' : 'default'} className="rounded-sm" data-testid="manage-subscription-link">
                <Crown className="w-4 h-4 mr-2" />
                {tier === 'premium' ? 'View Plans' : 'Upgrade'}
              </Button>
            </Link>
          </div>
        </div>

        {/* AI Features */}
        <div className="bg-card border border-border rounded-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Sparkle weight="fill" className={cn("w-5 h-5", user?.ai_enabled ? "text-accent" : "text-muted-foreground")} />
            <h2 className="font-heading font-bold text-lg">AI Features</h2>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="font-medium">AI Explanations</Label>
              <p className="text-xs text-muted-foreground">
                {tier === 'premium' 
                  ? 'Get detailed AI explanations for questions' 
                  : 'Upgrade to Premium to unlock'}
              </p>
            </div>
            <div className="relative">
              <Switch 
                checked={user?.ai_enabled || false} 
                onCheckedChange={handleAIToggle}
                disabled={tier !== 'premium' || loading}
                className={cn(user?.ai_enabled && "ai-glow")}
                data-testid="ai-toggle"
              />
              {tier !== 'premium' && (
                <Crown weight="fill" className="w-4 h-4 text-accent absolute -top-2 -right-2" />
              )}
            </div>
          </div>
        </div>

        {/* Reset Stats */}
        <div className="bg-card border border-destructive/30 rounded-sm p-6 mb-6" data-testid="reset-stats-section">
          <div className="flex items-center gap-3 mb-4">
            <Trash weight="fill" className="w-5 h-5 text-destructive" />
            <h2 className="font-heading font-bold text-lg">Reset Progress</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Clear all your practice history, exam results, streaks, and spaced repetition data. Bookmarks will be kept. This action cannot be undone.
          </p>
          {!showResetConfirm ? (
            <Button
              variant="outline"
              onClick={() => setShowResetConfirm(true)}
              className="rounded-sm border-destructive/50 text-destructive hover:bg-destructive hover:text-destructive-foreground"
              data-testid="reset-stats-button"
            >
              <Trash className="w-4 h-4 mr-2" />
              Reset All Stats
            </Button>
          ) : (
            <div className="bg-destructive/10 border border-destructive/30 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <Warning weight="fill" className="w-5 h-5 text-destructive" />
                <span className="font-bold text-destructive text-sm">Are you sure?</span>
              </div>
              <p className="text-xs text-muted-foreground mb-4">
                This will permanently delete all practice history, exam results, and streak data.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="destructive"
                  onClick={handleResetStats}
                  disabled={resetting}
                  className="rounded-sm"
                  data-testid="confirm-reset-button"
                >
                  {resetting ? (
                    <SpinnerGap className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Trash className="w-4 h-4 mr-2" />
                  )}
                  Yes, Reset Everything
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowResetConfirm(false)}
                  className="rounded-sm"
                  data-testid="cancel-reset-button"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Admin Panel Link */}
        {user?.role === 'admin' && (
          <div className="bg-card border border-border rounded-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <GearSix weight="fill" className="w-5 h-5 text-primary" />
              <h2 className="font-heading font-bold text-lg">Administration</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Manage questions, users, and app content
            </p>
            <Link to="/admin">
              <Button className="rounded-sm" data-testid="admin-panel-link">
                <GearSix className="w-4 h-4 mr-2" />
                Open Admin Panel
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
